import type { Maneuver } from '../lib/maneuvers';

/**
 * The arrow in the instruction banner.
 *
 * Drawn rather than shipped as an icon font: it is nine shapes, and a driver
 * reading it at a glance needs it heavy and unambiguous, which is easier to
 * guarantee with paths than by hoping a font renders at the right weight.
 *
 * All of them point up the screen and turn from there, matching the map's
 * heading-up orientation — an arrow that points north while the map points
 * along the road is a puzzle at exactly the wrong moment.
 */
export function ManeuverIcon({ maneuver, size = 34 }: { maneuver: Maneuver; size?: number }) {
  const stroke = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 3.4,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };

  // Each path runs from the bottom of the box (where the driver is) to the
  // direction of travel after the manoeuvre.
  const d: Record<Maneuver, string> = {
    depart: 'M16 27 V9',
    arrive: 'M16 27 V13',
    straight: 'M16 27 V8',
    'slight-left': 'M16 27 V17 L9 10',
    left: 'M16 27 V15 H7',
    'sharp-left': 'M16 27 V16 L7 22',
    'slight-right': 'M16 27 V17 L23 10',
    right: 'M16 27 V15 H25',
    'sharp-right': 'M16 27 V16 L25 22',
    'u-turn': 'M11 27 V16 A5 5 0 0 1 21 16 V22',
  };

  const head: Record<Maneuver, string> = {
    depart: 'M11 13 L16 8 L21 13',
    arrive: '',
    straight: 'M11 13 L16 8 L21 13',
    'slight-left': 'M9 15 L9 10 L14 10',
    left: 'M12 10 L7 15 L12 20',
    'sharp-left': 'M7 17 L7 22 L12 22',
    'slight-right': 'M23 15 L23 10 L18 10',
    right: 'M20 10 L25 15 L20 20',
    'sharp-right': 'M25 17 L25 22 L20 22',
    'u-turn': 'M17 18 L21 22 L25 18',
  };

  return (
    <svg viewBox="0 0 32 32" width={size} height={size} aria-hidden="true">
      <path d={d[maneuver]} {...stroke} />
      {head[maneuver] && <path d={head[maneuver]} {...stroke} />}
      {maneuver === 'arrive' && (
        <circle cx="16" cy="9" r="4.5" fill="currentColor" />
      )}
    </svg>
  );
}
